import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { paymeApi } from "../../lib/payme-api";
import styles from "./PaymeForm.module.css";
import "./payme-custom.css";
import SharedLoading from "../../components/SharedLoading/SharedLoading";

// Export the interface to make it available for import
export interface PaymeFormProps {
  customerData: {
    firstName: string;
    lastName: string;
    email: string;
    documentType: string;
    documentNumber: string;
    phone: string;
  };
  shippingData: {
    email: string;
    address: string;
    district: string;
    province: string;
    department: string;
    country: string;
    ubigeoCode: string;
  };
  amount: number;
  onSuccess: (response: any) => void;
  onError: (error: Error) => void;
  onStartProcessing?: () => void;
}

export interface PaymeFormRef {
  processPayment: () => Promise<void>;
  resetForm: () => void;
}

declare global {
  interface Window {
    FlexCapture: any;
  }
}

/**
 * PaymeForm - A component that renders the PayMe payment form
 *
 * This component has been refactored to simplify the initialization process
 * and improve error handling.
 */
const PaymeForm = forwardRef<PaymeFormRef, PaymeFormProps>(
  (
    {
      customerData,
      shippingData,
      amount,
      onSuccess,
      onError,
      onStartProcessing,
    },
    ref
  ) => {
    // State for tracking loading and initialization
    const [isLoading, setIsLoading] = useState(true);
    const [isInitialized, setIsInitialized] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [instanceKey] = useState(() => Date.now().toString());

    // Refs for tracking component lifecycle and preventing duplicate operations
    const isMounted = useRef(true);
    const captureInstanceRef = useRef<any>(null);
    const isInitializing = useRef(false);
    const formContainerRef = useRef<HTMLDivElement>(null);

    // Unique ID for the form container
    const formContainerId = `payme-form-container-${instanceKey}`;

    // Log the amount received by PaymeForm
    useEffect(() => {
      console.log("[PaymeForm] Received amount prop:", amount);
    }, [amount]);

    // Static resources loading - only run once per component instance
    useEffect(() => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      console.log(`PaymeForm (${instanceKey}): Component mounted`);

      // Flag to track if this effect's async operations should continue
      let isEffectActive = true;

      // Load CSS and JS only once
      const loadStaticResources = async () => {
        try {
          // Load CSS first
          await loadCSS();

          if (!isEffectActive) return;
          console.log(`PaymeForm (${instanceKey}): CSS loaded`);

          // Then load JS
          await loadScript();

          if (!isEffectActive) return;
          console.log(`PaymeForm (${instanceKey}): JS script loaded`);

          // Signal that static resources are loaded
          if (isEffectActive) {
            // Now we can proceed to get the nonce and initialize the form
            await getNonceAndInitialize();
          }
        } catch (err) {
          if (!isEffectActive) return;

          console.error(
            `PaymeForm (${instanceKey}): Error loading static resources`,
            err
          );
          handleError(err, "Failed to load payment form resources");
        }
      };

      // Start loading static resources
      loadStaticResources();

      // Cleanup function
      return () => {
        console.log(`PaymeForm (${instanceKey}): Component unmounting`);
        isEffectActive = false;
        isMounted.current = false;

        // Clean up FlexCapture instance if it exists
        if (captureInstanceRef.current) {
          try {
            captureInstanceRef.current = null;
          } catch (e) {
            console.error(
              `PaymeForm (${instanceKey}): Error cleaning up FlexCapture`,
              e
            );
          }
        }

        // Remove any elements we created
        try {
          const container = document.getElementById(formContainerId);
          if (container && container.parentNode) {
            container.parentNode.removeChild(container);
          }
        } catch (e) {
          console.error(
            `PaymeForm (${instanceKey}): Error removing form container`,
            e
          );
        }
      };
    }, []); // Empty dependency array - only run once

    // Function to get nonce and initialize form
    const getNonceAndInitialize = async () => {
      if (!isMounted.current) return;

      try {
        setIsLoading(true);

        // Get nonce token
        const nonceToken = await paymeApi.getNonce();

        if (!isMounted.current) return;
        console.log(`PaymeForm (${instanceKey}): Nonce received`);

        // Initialize form with nonce
        if (window.FlexCapture) {
          await initializeForm(nonceToken);
        }
      } catch (err) {
        if (!isMounted.current) return;

        console.error(
          `PaymeForm (${instanceKey}): Error in nonce/initialization`,
          err
        );
        handleError(err, "Failed to initialize payment form");
      }
    };

    // Helper function for error handling
    const handleError = (err: any, defaultMessage: string) => {
      if (!isMounted.current) return;

      setError(err instanceof Error ? err.message : defaultMessage);
      setIsLoading(false);
      onError(err instanceof Error ? err : new Error(defaultMessage));
    };

    // Helper function to load CSS
    const loadCSS = () => {
      return new Promise((resolve, reject) => {
        // Check if CSS is already loaded
        const existingLink = document.querySelector(
          'link[href*="flex-capture.css"]'
        );
        if (existingLink) {
          resolve(null);
          return;
        }

        const cssLink = document.createElement("link");
        cssLink.rel = "stylesheet";
        cssLink.type = "text/css";
        cssLink.href =
          "https://d23b52o2im4p82.cloudfront.net/css/flex-capture.css";
        cssLink.onload = resolve;
        cssLink.onerror = reject;
        document.head.appendChild(cssLink);
      });
    };

    // Helper function to load JavaScript
    const loadScript = () => {
      return new Promise((resolve, reject) => {
        // Check if script is already loaded
        if (window.FlexCapture) {
          resolve(null);
          return;
        }

        const existingScript = document.querySelector(
          'script[src*="flex-capture.min.js"]'
        );
        if (existingScript) {
          // If script exists but FlexCapture is not defined, wait for it
          const checkFlexCapture = setInterval(() => {
            if (window.FlexCapture) {
              clearInterval(checkFlexCapture);
              resolve(null);
            }
          }, 100);

          // Set a timeout to prevent infinite waiting
          setTimeout(() => {
            clearInterval(checkFlexCapture);
            if (!window.FlexCapture) {
              reject(new Error("FlexCapture failed to initialize"));
            }
          }, 5000);

          return;
        }

        const scriptEl = document.createElement("script");
        scriptEl.src =
          "https://d23b52o2im4p82.cloudfront.net/flex-capture.min.js";
        scriptEl.async = false;
        scriptEl.onload = resolve;
        scriptEl.onerror = reject;
        document.body.appendChild(scriptEl);
      });
    };

    // Add this effect after the form is initialized and not loading
    useEffect(() => {
      if (isInitialized && !isLoading && formContainerRef.current) {
        setTimeout(() => {
          try {
            // Add null check for formContainerRef.current
            const container = formContainerRef.current;
            if (!container) return;
            const form = container.querySelector("form");
            if (!form) return;
            const submitDiv = form.querySelector(".submit");
            const expiryCvvRow = form.querySelector(".expiry-cvv-row");
            if (
              submitDiv &&
              expiryCvvRow &&
              submitDiv.nextSibling !== expiryCvvRow
            ) {
              // Move .submit after .expiry-cvv-row
              form.insertBefore(submitDiv, expiryCvvRow.nextSibling);
            }
          } catch (err) {
            console.error("Error moving submit button:", err);
          }
        }, 200);
      }
    }, [isInitialized, isLoading]);

    // Initialize the payment form with the provided nonce
    const initializeForm = async (nonce: string) => {
      if (isInitializing.current || !isMounted.current) {
        return;
      }

      isInitializing.current = true;
      console.log(`PaymeForm (${instanceKey}): Initializing form with nonce`);

      try {
        // Wait for the form container ref to be available
        if (!formContainerRef.current) {
          console.log(
            `PaymeForm (${instanceKey}): Form container ref not available yet, waiting...`
          );

          // Wait a bit for the ref to be available
          await new Promise((resolve) => setTimeout(resolve, 500));

          // Check again after waiting
          if (!formContainerRef.current) {
            console.log(
              `PaymeForm (${instanceKey}): Form container ref still not available after waiting`
            );
            throw new Error("Form container ref is not available");
          }

          console.log(
            `PaymeForm (${instanceKey}): Form container ref is now available after waiting`
          );
        }

        // Format amount for Payme: last 5 digits are decimal part, pad with zeros
        const amountForPayme = (amount * 100000).toFixed(0).padStart(10, "0");
        console.log(
          "[PaymeForm] amount:",
          amount,
          "amountForPayme:",
          amountForPayme
        );

        const payRequest = {
          action: "authorize",
          transaction: {
            currency: "604",
            amount: amountForPayme,
            meta: {
              internal_operation_number: Math.floor(Date.now())
                .toString()
                .substring(7),
              description: "Descripcion de la transaccion",
              additional_fields: {
                2: "Ejemplo valor reservado 2",
                reserved1: "Ejemplo valor reservado 1",
              },
            },
          },
          address: {
            billing: {
              first_name: customerData.firstName,
              last_name: customerData.lastName,
              email: shippingData.email,
              phone: {
                country_code: "51",
                subscriber: customerData.phone,
              },
              location: {
                line_1: shippingData.address,
                line_2: shippingData.district,
                city: shippingData.province,
                state: shippingData.department,
                country: "PE",
                zip_code: shippingData.ubigeoCode,
              },
            },
          },
          card_holder: [
            {
              first_name: customerData.firstName,
              last_name: customerData.lastName,
              email_address: shippingData.email,
              identity_document_country: "PE",
              identity_document_type: customerData.documentType,
              identity_document_identifier: customerData.documentNumber,
            },
          ],
        };

        // Create FlexCapture instance
        const capture = new window.FlexCapture({
          nonce,
          payload: payRequest,
          additionalFields: [],
          layout: {
            useFlexRows: true,
          },
        });

        captureInstanceRef.current = capture;

        // Initialize the form
        capture.init(
          formContainerRef.current,
          handlePaymentResponse,
          handlePaymentStart,
          handlePaymentError
        );

        setIsInitialized(true);
        setIsLoading(false);
      } catch (error) {
        console.error("PaymeForm: Error initializing form", error);
        if (isMounted.current) {
          setError(
            error instanceof Error
              ? error.message
              : "Failed to initialize payment form"
          );
          setIsLoading(false);
          onError(
            error instanceof Error
              ? error
              : new Error("Failed to initialize payment form")
          );
        }
      } finally {
        isInitializing.current = false;
      }
    };

    // Handle payment response from FlexCapture
    const handlePaymentResponse = (response: any) => {
      if (!isMounted.current) return;

      console.log("PaymeForm: Payment response received", response);

      if (
        response &&
        response.transaction &&
        response.transaction.meta &&
        response.transaction.meta.status
      ) {
        const statusCode = response.transaction.meta.status.code;

        if (statusCode === "00") {
          onSuccess(response);
        } else {
          let errorMessage = "Error procesando el pago";

          if (
            response.transaction.meta.status.message_ilgn &&
            response.transaction.meta.status.message_ilgn.length > 0
          ) {
            errorMessage =
              response.transaction.meta.status.message_ilgn[0].value;
          }

          setError(errorMessage);
          onError(new Error(errorMessage));
        }
      } else {
        setError("Formato de respuesta inesperado");
        onError(new Error("Formato de respuesta inesperado"));
      }
    };

    // Handle payment start event
    const handlePaymentStart = () => {
      if (onStartProcessing && isMounted.current) {
        console.log("PaymeForm: Payment processing started");
        onStartProcessing();
      }
    };

    // Handle payment error event
    const handlePaymentError = () => {
      if (!isMounted.current) return;

      console.error("PaymeForm: Error during payment processing");
      setError("Error durante el procesamiento del pago");
      onError(new Error("Error durante el procesamiento del pago"));
    };

    // Process payment - called externally via ref
    const processPayment = async () => {
      console.log("PaymeForm: processPayment called");
      // The form automatically processes payment when the user submits it
      // This method exists for API consistency
    };

    // Reset form - called externally via ref
    const resetForm = async () => {
      console.log(`PaymeForm (${instanceKey}): resetForm called`);

      // Clear any errors
      setError(null);

      // Reset the form state
      if (captureInstanceRef.current) {
        captureInstanceRef.current = null;
      }

      setIsInitialized(false);
      setIsLoading(true);

      try {
        // Wait a moment for the DOM to update
        await new Promise((resolve) => setTimeout(resolve, 300));

        // Get a new nonce
        const newNonce = await paymeApi.getNonce();

        if (!isMounted.current) return;

        console.log(`PaymeForm (${instanceKey}): Got new nonce for reset`);

        // Wait a bit more to ensure the form container is ready
        await new Promise((resolve) => setTimeout(resolve, 300));

        if (!isMounted.current) return;

        // Now try to initialize with the new nonce
        await initializeForm(newNonce);
      } catch (error) {
        console.error(
          `PaymeForm (${instanceKey}): Failed to reset form:`,
          error
        );
        if (isMounted.current) {
          setError(
            error instanceof Error
              ? error.message
              : "Failed to reload payment form"
          );
          setIsLoading(false);
          onError(
            error instanceof Error
              ? error
              : new Error("Failed to reload payment form")
          );
        }
      }
    };

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      processPayment,
      resetForm,
    }));

    return (
      <div className={styles.paymeFormContainer}>
        {error && (
          <div className={styles.paymentErrorBox}>
            <p>{error}</p>
          </div>
        )}

        {/* Always render the container div with the ref, but conditionally show loading */}
        <div
          ref={formContainerRef}
          className={styles.paymentFormRoot}
          id={formContainerId}
          style={{ display: isLoading ? "none" : "block" }}
        />

        {isLoading && (
          <div className={styles.loadingContainer}>
            <SharedLoading
              size="medium"
              text="Cargando formulario de pago..."
            />
          </div>
        )}

        {!isInitialized && !isLoading && (
          <div className={styles.hiddenStatusContainer}>
            <span className={styles.initializingMessage}>
              Inicializando formulario de pago...
            </span>
          </div>
        )}
      </div>
    );
  }
);

PaymeForm.displayName = "PaymeForm";

export default PaymeForm;
